import { capitalize, sum } from 'lodash-es';
import { DateTime } from 'luxon';

import { DealInvoiceDueTypesEnum, DealTransactionTypeEnum } from '@/bundles/Deal/types';
import { BrokerPercentageType, IDealBrokerFormData } from '@/bundles/Brokers/types';
import { Nullable } from '@/utils/types';
import { formatJSDate, getDateDiff } from '@/utils/filters';

export * from './formData';

export const getDealContactsArray = (contactsObject: Record<string, any> | undefined) => {
  if (!contactsObject) return [];

  return Object.keys(contactsObject).reduce((acc, key) => {
    return acc.concat(contactsObject[key]);
  }, []);
};

export const getDealTransactionTypeItems = () => {
  return Object.values(DealTransactionTypeEnum).map(value => {
    return { text: capitalize(value), value };
  });
};

export const getBrokersSplitInfo = (brokers, totalCommission: Nullable<number>) => {
  const commission = totalCommission || 0;
  const offTopPercentage = sum(brokers.filter(broker => broker.percentage_type === BrokerPercentageType.offTop).map(broker => broker.percentage));
  const offTopTotal = commission * offTopPercentage / 100;
  const offRemainingTotal = commission - offTopTotal;

  return brokers.map(broker => {
    const total = broker.percentage_type === BrokerPercentageType.offTop ? commission : offRemainingTotal;

    return {
      broker: broker._id,
      name: broker.name,
      photo: broker.img,
      percentage: broker.percentage,
      percentage_type: broker.percentage_type,
      total: total * broker.percentage / 100,
    }
  })
};

const FULL_COMMISSION_PERCENTAGE = 100;
const COMMISSION_PERCENTAGE_TOLERANCE = 0.01;

export const TOTAL_COMMISSION_PERCENTAGE_ERROR = 'Total commission percentage must equal 100%.';

type CommissionSplit = Pick<IDealBrokerFormData, 'percentage' | 'percentage_type'>;

export const getTotalCommissionPercentage = (brokers: CommissionSplit[]): number => {
  const sumByType = (type: BrokerPercentageType) =>
    sum(brokers.filter(broker => broker.percentage_type === type).map(broker => broker.percentage || 0));

  const offTopPercentage = sumByType(BrokerPercentageType.offTop);
  const ofRemainingPercentage = sumByType(BrokerPercentageType.ofRemaining);

  return offTopPercentage + ofRemainingPercentage * (FULL_COMMISSION_PERCENTAGE - offTopPercentage) / FULL_COMMISSION_PERCENTAGE;
};

export const isTotalCommissionPercentageValid = (brokers: CommissionSplit[]): boolean => {
  return Math.abs(getTotalCommissionPercentage(brokers) - FULL_COMMISSION_PERCENTAGE) < COMMISSION_PERCENTAGE_TOLERANCE;
};

export const getInvoiceStatusProps = (invoice) => {
  const received = invoice.payment
          .map((payment) => payment.amount)
          .reduce((accum, value) => accum + value, 0);

  const isOverdue = getDateDiff(invoice.due_date).milliseconds < 0;
  const isPaid = invoice.amount - received <= 0

  // A CSS colour for an inline `color:` style; theme tokens so the status
  // stays readable on a dark card (CSS `red` was 4.2:1 on #1e1e1e).
  return {
    status: isPaid ? 'paid' : isOverdue ? 'overdue' : 'open',
    color: isPaid ? 'rgb(var(--v-theme-sc-text-paid))' : isOverdue ? 'rgb(var(--v-theme-sc-text-overdue))' : '',
  };
};

export const getInvoiceAging = (date: string): { daysDiff: Nullable<number>, diffLabel: Nullable<string> } => {
  if (!date) {
    return {
      daysDiff: null,
      diffLabel: null
    };
  }
  const today = DateTime.now();
  const due = DateTime.fromISO(formatJSDate(date, true) as string);
  const diff = Math.round(today.diff(due, 'days').days);
  let label: Nullable<string>;

  switch (true) {
    case (diff > 0 && diff <= 30):
      label = '1-30 days';
      break;
    case (diff > 30 && diff <= 60):
      label = '31 - 60 days';
      break;
    case (diff > 60 && diff <= 90):
      label = '61 - 90 days';
      break;
    case (diff > 90):
      label = '91+ days';
      break;
    default:
      label = null;
  }
  return {
    daysDiff: diff > 0 ? diff : null,
    diffLabel: label
  }
};

export function getInvoiceDueItems () {
  return [
    { text: 'Due on Receipt', value: DealInvoiceDueTypesEnum.on_receipt },
    { text: 'Net 10', value: DealInvoiceDueTypesEnum.on_10 },
    { text: 'Net 15', value: DealInvoiceDueTypesEnum.on_15 },
    { text: 'Net 30', value: DealInvoiceDueTypesEnum.on_30 },
  ];
}
